import { Skeleton, Table, TableBody, TableCell, TableHead, TableRow, useValidationResultDialog } from '@nest/uikit-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTerminalClient } from '../client-rpc-router';
import { TerminalScreen } from '../components/TerminalScreen';
import { useNumberParam } from '../helpers';

export default function PackageUnitDetailPage() {
	const client = useTerminalClient();
	const packageUnitId = useNumberParam('packageUnitId');

	const { data, error } = useQuery({
		queryKey: ['package-unit', packageUnitId],
		queryFn: () => client.consignmentNotes.getPackageUnitByIdQuery.execute({ id: packageUnitId! }),
		enabled: !!packageUnitId
	});

	useValidationResultDialog([error]);

	const itemsWithCounts = useMemo(() => {
		if (!data?.items) {
			return [];
		}

		const itemMap = new Map<string, { name: string; count: number }>();

		for (const item of data.items) {
			const existing = itemMap.get(item.itemName);
			if (existing) {
				existing.count += item.quantity;
			} else {
				itemMap.set(item.itemName, { name: item.itemName, count: item.quantity });
			}
		}

		return Array.from(itemMap.values());
	}, [data?.items]);

	return (
		<TerminalScreen showBackButton topBar={<div>{data?.parcelNumber ?? data?.consignmentNumber ?? 'Balík'}</div>}>
			<div className="p-2 flex flex-col gap-4">
				{data ? (
					<>
						<Table>
							<TableBody>
								{data.parcelNumber && (
									<TableRow>
										<TableHead className="w-1/2">Číslo balíku</TableHead>
										<TableCell className="w-1/2">{data.parcelNumber}</TableCell>
									</TableRow>
								)}
								{data.consignmentNumber && (
									<TableRow>
										<TableHead className="w-1/2">Číslo zásilky</TableHead>
										<TableCell className="w-1/2">{data.consignmentNumber}</TableCell>
									</TableRow>
								)}
								{data.recipientName && (
									<TableRow>
										<TableHead className="w-1/2">Příjemce</TableHead>
										<TableCell className="w-1/2">{data.recipientName}</TableCell>
									</TableRow>
								)}
								{data.carrierName && (
									<TableRow>
										<TableHead className="w-1/2">Dopravní služba</TableHead>
										<TableCell className="w-1/2">{data.carrierName}</TableCell>
									</TableRow>
								)}
								{data.packedAt && (
									<TableRow>
										<TableHead className="w-1/2">Zabalen</TableHead>
										<TableCell className="w-1/2">{new Date(data.packedAt).toLocaleString()}</TableCell>
									</TableRow>
								)}
								{data.packedByMachineName && (
									<TableRow>
										<TableHead className="w-1/2">Balící linka</TableHead>
										<TableCell className="w-1/2">{data.packedByMachineName}</TableCell>
									</TableRow>
								)}
								{data.packingLineDockLocationName && (
									<TableRow>
										<TableHead className="w-1/2">Dok</TableHead>
										<TableCell className="w-1/2">{data.packingLineDockLocationName}</TableCell>
									</TableRow>
								)}
								<TableRow>
									<TableHead className="w-1/2">
										Naskenován do
										<br />
										kontejneru
									</TableHead>
									<TableCell className="w-1/2">{data.scannedAt ? new Date(data.scannedAt).toLocaleString() : 'ne'}</TableCell>
								</TableRow>
								{data.containerNumber && (
									<TableRow>
										<TableHead className="w-1/2">Kontejner</TableHead>
										<TableCell className="w-1/2">{data.containerNumber}</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>

						<div className="border-t pt-4">
							<div className="font-medium mb-2">Položky v balíku</div>
							{itemsWithCounts.length === 0 ? (
								<div className="p-6 text-center text-muted-foreground">Žádné položky</div>
							) : (
								<div className="flex flex-col gap-2">
									{itemsWithCounts.map((item, index) => (
										<div key={index} className="border rounded p-2">
											<div className="font-medium">{item.name}</div>
											<div className="text-muted-foreground">Množství: {item.count}</div>
										</div>
									))}
								</div>
							)}
						</div>
					</>
				) : (
					<div className="space-y-2">
						<Skeleton className="h-6 w-full" />
						<Skeleton className="h-6 w-full" />
						<Skeleton className="h-6 w-full" />
					</div>
				)}
			</div>
		</TerminalScreen>
	);
}
